import { EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useMemo } from 'react';

import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

export const QueueDashboardEmpty = (): JSX.Element => {
  const navigate = useAppNavigate();

  const { params } = useAppSearchParams('/dashboard/queues');

  const emptyState = useMemo((): JSX.Element => {
    if (params.q) {
      return (
        <EmptyState
          title="No queues found"
          description="Try adjusting your search to find what you're looking for."
          icon="search"
        />
      );
    }

    switch (params.type) {
      case 'agent':
        return (
          <EmptyState
            title="You are not assigned to any queues."
            description="Queues that you are assigned to will appear here."
            icon="user"
            buttonProps={{
              children: 'See all queues',
              onClick: () => {
                ampli.dashboardQueuesTypeChanged({
                  type: 'all',
                  location: 'queuesMessageButton',
                });

                navigate({
                  path: '/dashboard/queues',
                  params: {
                    search: {
                      type: 'all',
                    },
                  },
                });
              },
            }}
          />
        );

      case 'favourite':
        if (!params.id) {
          return (
            <EmptyState
              title="You have not selected any favorite list."
              description="Select a favorite list from the top-right dropdown to see your favorite queues."
              icon="star"
            />
          );
        }

        return (
          <EmptyState
            title="You do not have any favorite queues."
            description="Click on the star icon next to a queue to add it to your favorites."
            icon="star"
            buttonProps={{
              children: 'See assigned queues',
              onClick: () => {
                ampli.dashboardQueuesTypeChanged({
                  type: 'agent',
                  location: 'favoritesMessageButton',
                });

                navigate({
                  path: '/dashboard/queues',
                  params: {
                    search: {
                      type: 'agent',
                    },
                  },
                });
              },
            }}
          />
        );

      case 'archived':
        return (
          <EmptyState
            title="There are no archived queues."
            description="Queues that have been archived will appear here."
            icon="archive"
            buttonProps={{
              children: 'See all queues',
              onClick: () => {
                ampli.dashboardQueuesTypeChanged({
                  type: 'all',
                  location: 'archivedQueuesMessageButton',
                });

                navigate({
                  path: '/dashboard/queues',
                  params: {
                    search: {
                      type: 'all',
                    },
                  },
                });
              },
            }}
          />
        );

      case 'all':
      default:
        return (
          <EmptyState
            title="There are no queues."
            description="Your organization does not have any queues yet."
            icon="info"
            buttonProps={{
              children: 'Go to Settings',
              onClick: () => navigate({ path: '/settings/queues', params: {} }),
            }}
          />
        );
    }
  }, [params, navigate]);

  return (
    <FlexBox
      flexDirection="column"
      height="100%"
      alignItems="center"
      justifyContent="center"
    >
      {emptyState}
    </FlexBox>
  );
};
